import { createAsyncThunk } from '@reduxjs/toolkit';
import { InheritedProductPackageAssignment, LicenseFormatted } from 'common/types/Entities';
import { EntitiesStatus } from 'common/types/Entities';
import { AclEntriesStatusTooltipProps } from 'common/types/TypeDefs';
import { getConditionsToDisplay } from 'common/utils/AclConditionsHelpers';
import { _getAclEnriesStatusTooltipDates, _removeAclEntriesStatusTooltipEmptyDates, calculateAclEntriesStatus } from 'common/utils/AclEntriesStatusTooltipHelpers';
import { dateToFrontEndFormat } from 'common/utils/dateHelpers';
import { AclEntriesPage } from 'features/aclEntries/aclEntriesTypeDefs';
import { PackageAssignmentPage } from 'features/productPackageAssignment/productPackageAssignmentTypeDefs';
import { IUserProfileTenant } from 'features/userProfileApps/UserProfileApps';

import { PackageAssignment } from '../../../types/Entities';
import { handleListFetch } from '../apiHelpers';
import { APIBaseResponse } from '../apiTypes';
import { APILicensesGet } from '../Licenses/Licenses.api';
import { _formatLicenseResponse } from '../Licenses/LicenseThunkHelpers';

import { APIInheritedPackageAssignmentsGetList, APIPackageAssignmentDelete, APIPackageAssignmentDeleteParams, APIPackageAssignmentPost, APIPackageAssignmentPostParams, APIPackageAssignmentPut, APIPackageAssignmentPutParams, APIPackageAssignmentsGetList } from './PackageAssignments.api';

export type FormattedPackageAssignment = PackageAssignment & {
    id: string;
    conditionsToDisplay: string;
    startDate: string;
    endDate: string;
};

export type FormattedInheritedPPA = InheritedProductPackageAssignment & {
    id: string;
    conditionsToDisplay: string;
    startDate: string;
    endDate: string;
};

export type FormattedInheritedPPAWithLicenseInfo = FormattedInheritedPPA & {
    licenseBillingRecord: string;
    status: EntitiesStatus;
    statusTooltip: AclEntriesStatusTooltipProps;
    currentPage: AclEntriesPage;
};

const formatInheritedPPAs = (rows: InheritedProductPackageAssignment[]): FormattedInheritedPPA[] => {
    return rows.map((item) => ({
        ...item,
        id: `${item.tenantId}.${item.productPackageId}.${item.userId}.${item.userGroupId ?? ''}.${item.licenseId ?? ''}`,
        conditionsToDisplay: getConditionsToDisplay(item.conditions),
        startDate: item.startDate ? dateToFrontEndFormat(item.startDate) : '',
        endDate: item.endDate ? dateToFrontEndFormat(item.endDate) : '',
    }));
};

const _mapInheritedPPAWithLicenseInfo = (ppas: FormattedInheritedPPA[], licenses: LicenseFormatted[], currentUserProfileTenant: IUserProfileTenant): FormattedInheritedPPAWithLicenseInfo[] => {
    return ppas.map((ppa) => {
        const license = licenses.find((l) => l.id === ppa.licenseId);
        const licenseBillingRecord = license?.billingRecord ?? '';
        const tooltipDates = _getAclEnriesStatusTooltipDates(ppa, currentUserProfileTenant, license);
        const filteredDates = _removeAclEntriesStatusTooltipEmptyDates(tooltipDates);
        const { tooltipProps, status } = calculateAclEntriesStatus(filteredDates, AclEntriesPage.UserProfileProductPackageAssignment);

        return {
            ...ppa,
            licenseBillingRecord,
            status,
            statusTooltip: tooltipProps,
            currentPage: AclEntriesPage.UserProfileProductPackageAssignment,
        };
    });
};

const formatPackageAssignments = (rows: PackageAssignment[]): FormattedPackageAssignment[] => {
    return rows.map((item) => ({
        ...item,
        id: `${item.tenantId}.${item.productPackageId}.${item.userGroupId || item.userId || ''}.${item.licenseId ?? ''}`,
        conditionsToDisplay: getConditionsToDisplay(item.conditions),
        startDate: item.startDate ? dateToFrontEndFormat(item.startDate) : '',
        endDate: item.endDate ? dateToFrontEndFormat(item.endDate) : '',
    }));
};

const _mapPPAWithLicenseInfo = (ppas: FormattedPackageAssignment[], licenses: LicenseFormatted[], AclEntriesPageType: AclEntriesPage) => {
    return ppas.map((ppa) => {
        const license = licenses.find((l) => l.id === ppa.licenseId);
        const licenseBillingRecord = license?.billingRecord ?? '';
        const tooltipDates = _getAclEnriesStatusTooltipDates(ppa, undefined, license);
        const filteredDates = _removeAclEntriesStatusTooltipEmptyDates(tooltipDates);
        const { tooltipProps, status } = calculateAclEntriesStatus(filteredDates, AclEntriesPageType);

        return {
            ...ppa,
            licenseBillingRecord,
            status,
            statusTooltip: tooltipProps,
            currentPage: AclEntriesPageType,
        };
    });
};

export const fetchUserProfilePackageAssignments = createAsyncThunk(
    'productPackages/fetchUserProfilePackageAssignments',
    async (item: { tenantId: string; userId: string; signal?: AbortSignal }) => {
        const response = await APIInheritedPackageAssignmentsGetList(item);
        return formatInheritedPPAs(response.data);
    }
);

export const fetchUserProfilePackageAssignmentsWithLicenseInfo = createAsyncThunk(
    'productPackages/fetchUserProfilePackageAssignmentsWithLicenseInfo',
    async (item: { currentUserProfileTenant: IUserProfileTenant; userId: string; signal?: AbortSignal }) => {
        const { currentUserProfileTenant, userId } = item;
        const { id: tenantId } = currentUserProfileTenant;
        const [ppaResponse, licensesResponse] = await Promise.all([
            APIInheritedPackageAssignmentsGetList({ tenantId, userId }),
            APILicensesGet({ tenantId }),
        ]);

        const ppas = formatInheritedPPAs(ppaResponse.data);
        const licenses = _formatLicenseResponse(licensesResponse.data);
        return _mapInheritedPPAWithLicenseInfo(ppas, licenses, currentUserProfileTenant);
    }
);

export const fetchPackageAssignments = createAsyncThunk('productPackages/fetchPackageAssignments', async (item: { tenantId: string, licenseId?: string, userId?:string, signal?: AbortSignal }) => {
    const response = await APIPackageAssignmentsGetList(item);
    return formatPackageAssignments(response.data);
});

export const fetchUserPackageAssignments = createAsyncThunk(
    'productPackages/fetchUserPackageAssignments',
    async (item: { tenantId: string; userId: string; signal?: AbortSignal }) => {
        const response = await APIPackageAssignmentsGetList(item);
        return formatPackageAssignments(response.data);
    }
);

export const fetchUserPackageAssignmentsWithLicenseInfo = createAsyncThunk(
    'productPackages/fetchUserPackageAssignmentsWithLicenseInfo',
    async (item: { tenantId: string; userId: string; signal?: AbortSignal }) => {
        const { tenantId, userId } = item;
        const [ppaResponse, licensesResponse] = await Promise.all([
            APIPackageAssignmentsGetList({ tenantId, userId }),
            APILicensesGet({ tenantId }),
        ]);

        const ppas = formatPackageAssignments(ppaResponse.data);
        const licenses = _formatLicenseResponse(licensesResponse.data);
        return _mapPPAWithLicenseInfo(ppas, licenses, AclEntriesPage.UserProductPackageAssignment);
    }
);

export const fetchUserGroupPackageAssignments = createAsyncThunk(
    'productPackages/fetchUserGroupPackageAssignments',
    async (item: { tenantId: string; userGroupId: string; signal?: AbortSignal }) => {
        const response = await APIPackageAssignmentsGetList(item);
        return formatPackageAssignments(response.data);
    }
);

export const fetchUserGroupPackageAssignmentsWithLicenseInfo = createAsyncThunk(
    'productPackages/fetchUserGroupPackageAssignmentsWithLicenseInfo',
    async (item: { tenantId: string; userGroupId: string; signal?: AbortSignal }) => {
        const { tenantId, userGroupId } = item;
        const [ppaResponse, licensesResponse] = await Promise.all([
            APIPackageAssignmentsGetList({ tenantId, userGroupId }),
            APILicensesGet({ tenantId }),
        ]);

        const ppas = formatPackageAssignments(ppaResponse.data);
        const licenses = _formatLicenseResponse(licensesResponse.data);
        return _mapPPAWithLicenseInfo(ppas, licenses, AclEntriesPage.UserGroupProductPackageAssignment);
    }
);

export const deleteUserGroupPackageAssignment = createAsyncThunk(
    'productPackages/deleteUserGroupPackageAssignment',
    async (item: APIPackageAssignmentDeleteParams & { withLicenseInfo: boolean }) => {
        const { tenantId, userGroupId, withLicenseInfo } = item;
        await APIPackageAssignmentDelete(item);

        if (withLicenseInfo) {
            const [ppas, licensesResponse] = await Promise.all([
                handleListFetch(APIPackageAssignmentsGetList({ tenantId, userGroupId })),
                APILicensesGet({ tenantId }),
            ]);
            const licenses = _formatLicenseResponse(licensesResponse.data);
            return _mapPPAWithLicenseInfo(formatPackageAssignments(ppas), licenses, AclEntriesPage.UserGroupProductPackageAssignment);
        }

        const rows = await handleListFetch(APIPackageAssignmentsGetList({ tenantId, userGroupId }));
        return formatPackageAssignments(rows);
    }
);

export const deleteLicensePackageAssignment = createAsyncThunk(
    'productPackages/deleteLicensePackageAssignment',
    async (item: APIPackageAssignmentDeleteParams) => {
        await APIPackageAssignmentDelete(item);

        const rows = await handleListFetch(APIPackageAssignmentsGetList({ tenantId: item.tenantId, licenseId: item.licenseId }));
        return formatPackageAssignments(rows);
    }
);

export const deleteUserProfilePackageAssignment = createAsyncThunk(
    'productPackages/deleteUserProfilePackageAssignment',
    async (item: APIPackageAssignmentDeleteParams) => {
        await APIPackageAssignmentDelete(item);
    }
);

export const deleteUserPackageAssignment = createAsyncThunk(
    'productPackages/deleteUserPackageAssignment',
    async (item: APIPackageAssignmentDeleteParams & { withLicenseInfo: boolean }) => {
        const { tenantId, userId, withLicenseInfo } = item;       
        await APIPackageAssignmentDelete(item);

        if (withLicenseInfo) {
            const [ppas, licensesResponse] = await Promise.all([
                handleListFetch(APIPackageAssignmentsGetList({ tenantId, userId })),
                APILicensesGet({ tenantId }),
            ]);
            const licenses = _formatLicenseResponse(licensesResponse.data);
            return _mapPPAWithLicenseInfo(formatPackageAssignments(ppas), licenses, AclEntriesPage.UserProductPackageAssignment);
        }

        const rows = await handleListFetch(APIPackageAssignmentsGetList({ tenantId: item.tenantId, userId: item.userId }));
        return formatPackageAssignments(rows);
    }
);

export type PostPackageAssignmentThunkParams = APIPackageAssignmentPostParams & {
    page: PackageAssignmentPage;
};

export type PackageAssignmentThunkResponse = APIBaseResponse<FormattedPackageAssignment[]>;

export const postPackageAssignment = createAsyncThunk(
    'productPackages/postPackageAssignment',
    async (item: PostPackageAssignmentThunkParams) => {
        let response = await APIPackageAssignmentPost(item);
        const rows = await getPackageAssignments(item.page, item.tenantId, item.userId, item.userGroupId, item.licenseId);
        const result = {
            error: response.error,
            success: response.success,
            data: rows
        };
        return result;
    }
);

export type PutPackageAssignmentThunkParams = APIPackageAssignmentPutParams & {
    page: PackageAssignmentPage;
};

export const putPackageAssignment = createAsyncThunk(
    'productPackages/putPackageAssignment',
    async (item: PutPackageAssignmentThunkParams) => {
        let response = await APIPackageAssignmentPut(item);
        const rows = await getPackageAssignments(item.page, item.tenantId, item.userId, item.userGroupId, item.licenseId);
        const result = {
            error: response.error,
            success: response.success,
            data: rows
        };
        return result;
    }
);

async function getPackageAssignments(page: string, tenantId: string, userId?: string, userGroupId?: string, licenseId?: string) {
    let data = [] as PackageAssignment[];
    if (page === PackageAssignmentPage.User) {
        if (licenseId === undefined){
            data = 
                (await APIPackageAssignmentsGetList({
                    tenantId: tenantId,
                    userId: userId,
                })).data;
        } else {
            const [ppaResponse, licensesResponse] = await Promise.all([
                APIPackageAssignmentsGetList({ tenantId, userId }),
                APILicensesGet({ tenantId }),
            ]);
            const ppas = formatPackageAssignments(ppaResponse.data);
            const licenses = _formatLicenseResponse(licensesResponse.data);
            return _mapPPAWithLicenseInfo(ppas, licenses, AclEntriesPage.UserProductPackageAssignment);
        }
            
    } else if (page === PackageAssignmentPage.UserGroup) {
        if (licenseId === undefined){
            data = 
                (await APIPackageAssignmentsGetList({
                    tenantId: tenantId,
                    userGroupId: userGroupId
                })).data;
            
        } else {
            const [ppaResponse, licensesResponse] = await Promise.all([
                APIPackageAssignmentsGetList({ tenantId, userGroupId }),
                APILicensesGet({ tenantId }),
            ]);

            const ppas = formatPackageAssignments(ppaResponse.data);
            const licenses = _formatLicenseResponse(licensesResponse.data);
            return _mapPPAWithLicenseInfo(ppas, licenses, AclEntriesPage.UserGroupProductPackageAssignment);
        }
    } else if (page === PackageAssignmentPage.License){
        data = 
            (await APIPackageAssignmentsGetList({
                tenantId: tenantId,
                licenseId: licenseId,
            })).data;
    } 
        
    const rows = formatPackageAssignments(data);
    return rows;
}